import * as React from "react";
import { GenerateDialog } from "@/new-pages/proving-ground/generate-dialog";
import { Box, Card, Chip, Stack, Tooltip } from "@mui/material";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { PencilSimpleIcon } from "@phosphor-icons/react/dist/ssr/PencilSimple";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import { SparkleIcon } from "@phosphor-icons/react/dist/ssr/Sparkle";
import { TrashIcon } from "@phosphor-icons/react/dist/ssr/Trash";
import { useNavigate } from "react-router-dom";

import { Persona, personasKeys, useDeletePersona } from "@/hooks/api/use-personas";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { useDialog } from "@/hooks/use-dialog";
import { CustomTable } from "@/components/customTable";
import { TableActions } from "@/components/customTable/table-actions";
import { Column, FilterOption, FilterType } from "@/components/customTable/types";
import { DeleteModal } from "@/components/deleteModal/delete-modal";
import { TableAnimation } from "@/components/table-animation/table-animation";

const SERVICE_LABELS: Record<string, string> = {
	stt: "Speech to text",
	llm: "Model",
	tts: "Voice",
};

export function Personas(): React.JSX.Element {
	const {
		items: personas,
		isLoading,
		totalItems,
		refetchData: fetchPersonas,
	} = useCachedTableStateWithUrl<Persona>({
		queryKey: personasKeys.lists(),
		endpoint: "/personas",
	});

	const navigate = useNavigate();
	const deletePersonaMutation = useDeletePersona();
	const [generateOpen, setGenerateOpen] = React.useState(false);

	const {
		data: selectedPersona,
		open: isDeleteModalOpen,
		handleClose: handleDeleteModalClose,
		handleOpen: handleDeleteModalOpen,
	} = useDialog<Persona | null>();

	const handleDelete = async () => {
		if (!selectedPersona) return;
		try {
			await deletePersonaMutation.mutateAsync(selectedPersona.id);
			handleDeleteModalClose();
		} catch {
			// Reported by the mutation.
		}
	};

	// `name` is the query parameter the API receives; "general" is a real choice there, not the
	// absence of one, so it gets an option rather than being the empty state.
	const filtersOptions: FilterOption[] = React.useMemo(
		() => [
			{
				name: "agentId",
				label: "Built for",
				value: "",
				type: FilterType.FETCH_SELECT,
				getOptionsUrl: "/agents",
				fetchLabel: "name",
				fetchValue: "id",
			},
		],
		[]
	);

	const columns: Column[] = React.useMemo(
		() => [
			{
				header: { id: "agentName", label: "Built for", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "agentName",
				getCellValue: (row: any) =>
					row.agentName ? (
						<Chip size="small" variant="outlined" label={row.agentName} />
					) : (
						<Tooltip title="Suits any agent — this caller carries no goal of its own." arrow>
							<Chip size="small" variant="outlined" color="default" label="General" />
						</Tooltip>
					),
			},
			{
				header: { id: "name", label: "Name", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "name",
				getCellValue: (row: any) => (
					<Tooltip title="Edit persona" arrow>
						<Box
							tabIndex={0}
							onClick={() => navigate(`/personas/${row.id}`)}
							sx={{
								textDecoration: "none",
								color: "inherit",
								cursor: "pointer",
								width: "max-content",
								"&:hover": { textDecoration: "underline" },
							}}
						>
							{row.name}
						</Box>
					</Tooltip>
				),
			},
			{
				header: { id: "description", label: "Description", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "description",
				getCellValue: (row: any) => (
					<Typography variant="body2" color="text.secondary">
						{row.description || "—"}
					</Typography>
				),
			},
			{
				// Which providers this caller uses is the thing most worth checking at a glance:
				// a persona sharing the agent's voice or model is the usual configuration mistake.
				header: { id: "services", label: "Providers", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "services",
				getCellValue: (row: any) => (
					<Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
						{(row.services ?? []).map((service: any) => (
							<Chip
								key={service.type}
								size="small"
								variant="outlined"
								label={`${SERVICE_LABELS[service.type] ?? service.type}: ${service.plugin?.title ?? "—"}`}
							/>
						))}
						{row.services?.length ? null : (
							<Typography variant="body2" color="text.secondary">
								Not configured
							</Typography>
						)}
					</Stack>
				),
			},
			{
				header: { id: "actions", label: "", width: 100, align: "left", sx: { fontWeight: "600" } },
				accessorKey: "actions",
				getCellValue: (row: any) => (
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
						<TableActions
							actions={[
								{
									label: "Edit",
									icon: <PencilSimpleIcon width={20} height={20} weight="fill" />,
									onClick: () => navigate(`/personas/${row.id}`),
								},
								{
									label: "Delete",
									icon: <TrashIcon width={20} height={20} weight="fill" />,
									sx: { color: "error.main" },
									onClick: () => handleDeleteModalOpen(row),
								},
							]}
						/>
					</Box>
				),
			},
		],
		[]
	);

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={3}
						sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
					>
						<Box sx={{ flex: "1 1 auto" }}>
							<Typography variant="h4">Personas</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
								Simulated callers for the Proving Ground. Each one is reusable across every agent.
							</Typography>
						</Box>
						<Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
							<Button startIcon={<SparkleIcon />} variant="outlined" onClick={() => setGenerateOpen(true)}>
								Generate
							</Button>
							<Button startIcon={<PlusIcon />} variant="contained" onClick={() => navigate("/personas/create")}>
								Create Persona
							</Button>
						</Stack>
					</Stack>
					<TableAnimation delay={450}>
						<Card>
							<CustomTable
								columns={columns}
								data={personas}
								refetchData={fetchPersonas}
								isLoading={isLoading}
								totalItems={totalItems}
								filtersOptions={filtersOptions}
							/>
						</Card>
					</TableAnimation>
				</Stack>
			</Box>

			<GenerateDialog
				kind="personas"
				open={generateOpen}
				onClose={() => setGenerateOpen(false)}
				onSaved={fetchPersonas}
			/>

			<DeleteModal
				open={isDeleteModalOpen}
				onClose={handleDeleteModalClose}
				onDelete={handleDelete}
				title="Delete Persona"
				description="Are you sure you want to delete this persona? Runs that already used it keep their results."
			/>
		</React.Fragment>
	);
}
